/**
 * Tenant Routes
 *
 * Titles/descriptions/breadcrumbs are auto-resolved by the i18n router
 * guard from each route's `name` — see `src/plugins/i18n/routeI18n.ts`.
 */

import { Role } from '../../types'

// Static imports for frequently used components
import TenantInfoOverview from '../../pages/settings/tenant/info/InfoOverview.vue'
import TenantLayoutOverview from '../../pages/settings/tenant/layout/LayoutOverview.vue'
import TenantTranslationOverview from '../../pages/settings/tenant/i18n/TranslationOverview.vue'
import TenantLanguageMenu from '../../pages/settings/tenant/i18n/LanguageMenu.vue'
import TenantTranslationsQuestionsOverview from '../../pages/settings/tenant/i18n/questions_options/QuestionsOverview.vue'
import TenantQuestionDetailView from '../../pages/settings/tenant/i18n/questions_options/QuestionDetailView.vue'
import TenantTranslationOptionsDetailView from '../../pages/settings/tenant/i18n/questions_options/OptionsDetailView.vue'
import TenantTranslationFaqCategoriesEdit from '../../pages/settings/tenant/i18n/faq/FaqCategories/EditCategory.vue'
import TenantFaqOverview from '../../pages/settings/tenant/i18n/faq/FaqOverview.vue'
import TenantFaqQAOverview from '../../pages/settings/tenant/i18n/faq/FaqQA/FaqOverview.vue'
import TenantTranslationFaqDetailView from '../../pages/settings/tenant/i18n/faq/FaqQA/FaqDetailView.vue'
import TenantTranslationFaqCategoriesView from '../../pages/settings/tenant/i18n/faq/FaqCategories/CategoriesOverview.vue'
import TenantTranslationEvaluationsCategoryDetailView from '../../pages/settings/tenant/i18n/evaluation/EvaluationDetailView.vue'
import TenantTranslationEvaluationCategoryOverview from '../../pages/settings/tenant/i18n/evaluation/CategoryOverview.vue'
import TenantStaticTextsOverview from '../../pages/settings/tenant/i18n/staticTexts/StaticTextsOverview.vue'
import TenantStaticTextsDetailView from '../../pages/settings/tenant/i18n/staticTexts/StaticTextsDetailView.vue'
import TenantSettingsOverview from '../../pages/settings/tenant/settings/Overview.vue'
import TenantSettingsGeneral from '../../pages/settings/tenant/settings/GeneralSettings.vue'
import TenantSettingsModules from '../../pages/settings/tenant/settings/ModulesSettings.vue'
import TenantSettingsTranslations from '../../pages/settings/tenant/settings/TranslationsSettings.vue'

const tenantAdminRoles: Role[] = ['ai_lab_admin', 'mandant_admin']

export default [
  // Tenant Info
  {
    path: '/tenant/info',
    name: 'tenant.info',
    component: TenantInfoOverview,
    meta: { requiresAuth: true }
  },

  // Tenant Layout
  {
    path: '/tenant/layout',
    name: 'tenant.layout',
    component: TenantLayoutOverview,
    meta: { requiresAuth: true }
  },

  // Tenant Translations Overview
  {
    path: '/tenant/translations',
    name: 'tenant.translations.overview',
    component: TenantTranslationOverview,
    meta: { requiresAuth: true }
  },
  {
    path: '/tenant/translations/:translation',
    name: 'tenant.translations.language',
    component: TenantLanguageMenu,
    meta: { requiresAuth: true }
  },

  // Tenant Translations - Questions & Options
  {
    path: '/tenant/translations/:translation/questions_options',
    name: 'tenant.translations.questionsOptions',
    component: TenantTranslationsQuestionsOverview,
    meta: { requiresAuth: true }
  },
  {
    path: '/tenant/translations/:translation/questions_options/question/:questionID',
    name: 'tenant.translations.questionDetail',
    component: TenantQuestionDetailView,
    meta: { requiresAuth: true }
  },
  {
    path: '/tenant/translations/:translation/questions_options/options/:questionID',
    name: 'tenant.translations.optionsDetail',
    component: TenantTranslationOptionsDetailView,
    meta: { requiresAuth: true }
  },

  // Tenant Translations - FAQ
  {
    path: '/tenant/translations/:translation/faq',
    name: 'tenant.translations.faq.overview',
    component: TenantFaqOverview,
    meta: { requiresAuth: true }
  },
  {
    path: '/tenant/translations/:translation/faq/qa',
    name: 'tenant.translations.faq.qa',
    component: TenantFaqQAOverview,
    meta: { requiresAuth: true }
  },
  {
    path: '/tenant/translations/:translation/faq/qa/edit/:faqID',
    name: 'tenant.translations.faq.qaDetail',
    component: TenantTranslationFaqDetailView,
    meta: { requiresAuth: true }
  },
  {
    path: '/tenant/translations/:translation/faq/categories/all',
    name: 'tenant.translations.faq.categories',
    component: TenantTranslationFaqCategoriesView,
    meta: { requiresAuth: true }
  },
  {
    path: '/tenant/translations/:translation/faq/categories/edit/:category',
    name: 'tenant.translations.faq.categoryEdit',
    component: TenantTranslationFaqCategoriesEdit,
    meta: { requiresAuth: true }
  },

  // Tenant Translations - Evaluation
  {
    path: '/tenant/translations/:translation/evaluation',
    name: 'tenant.translations.evaluation.overview',
    component: TenantTranslationEvaluationCategoryOverview,
    meta: { requiresAuth: true }
  },
  {
    path: '/tenant/translations/:translation/evaluation/:category',
    // Preserved literal name — referenced by router.push() in CategoryOverview.vue.
    name: 'EvaluationCategoryDetailView',
    component: TenantTranslationEvaluationsCategoryDetailView,
    meta: { requiresAuth: true }
  },

  // Tenant Translations - Static Texts
  {
    path: '/tenant/translations/:translation/staticTexts',
    name: 'tenant.translations.staticTexts.overview',
    component: TenantStaticTextsOverview,
    meta: { requiresAuth: true }
  },
  {
    path: '/tenant/translations/:translation/staticTexts/:staticTextKey',
    // Preserved literal name — referenced by router.push() in StaticTextsOverview.vue.
    name: 'StaticTextsDetailView',
    component: TenantStaticTextsDetailView,
    meta: { requiresAuth: true }
  },

  // Tenant Settings
  {
    path: '/tenant/settings',
    name: 'tenant.settings.section',
    meta: { requiresAuth: true, roles: tenantAdminRoles },
    children: [
      {
        path: '',
        name: 'tenant.settings.overview',
        component: TenantSettingsOverview
      },
      {
        path: 'general',
        name: 'tenant.settings.general',
        component: TenantSettingsGeneral
      },
      {
        path: 'modules',
        name: 'tenant.settings.modules',
        component: TenantSettingsModules
      },
      {
        path: 'translations',
        name: 'tenant.settings.translations',
        component: TenantSettingsTranslations
      }
    ]
  }
]
